import React, { CSSProperties, memo, useMemo } from 'react';
import classNames from 'classnames';
import s from '../../Grid.module.less';
import { GridRow } from './GridRow';
import { Identifiable } from '@custis/common';
import { ColumnInstance, IdType, Row } from 'react-table';
import { TTableRowMode } from '../../models';
import { areEqual } from 'react-window';
import { observer } from 'mobx-react';
import { NewRow } from '../row-fields';

export interface IGridRowData<T extends Identifiable> {
  rows: Row<T>[];
  highlightedId: string | undefined;
  tableRowMode: TTableRowMode;
  totalColumnsWidth: number;
  allColumns: ColumnInstance<T>[];
  editRowData: Partial<T>;
  newRowData: Partial<T>;
  subRowColumns: ColumnInstance<T>[];
  subRowId?: number;
  hiddenColumns: IdType<T>[];

  prepareRow(row: Row<T>): void;

  onClickRow(id: string): void;

  onRowDoubleClick(data: T, gridRowId: string): void;

  handleEditRow(row: T): Promise<void>;

  handleCreateSubRow(row: T): Promise<void>;

  handleEditSubRow(row: T): Promise<void>;

  onEditRowChange(row: Partial<T>): void;

  onNewRowChange(row: Partial<T>): void;

  onHideEditRow(): void;

  getRowStyle(originalRow: T): CSSProperties | undefined;
}

interface Props<T extends Identifiable> {
  index: number;
  style?: CSSProperties;
  data: IGridRowData<T>;
}

const newSubRowKey = 'newSubRowKey';

const GridRowRendererInner = observer(<T extends Identifiable>({ index, style, data }: Props<T>) => {
  const row = data.rows[index];
  data.prepareRow(row);
  const rowProps = row.getRowProps({ style });
  const rowStyle = useMemo(() => data.getRowStyle(row.original), [row, data]) ?? {};
  rowProps.style = {
    ...rowProps.style,
    width: undefined,
    ...rowStyle,
  };
  const rowHighlighted = useMemo(() => row.id === data.highlightedId, [row, data]);
  const editEnabled = useMemo(
    () => rowHighlighted && data.tableRowMode === 'edit' && row.depth === 0,
    [rowHighlighted, data, row],
  );
  const subRowEditEnabled = useMemo(
    () => rowHighlighted && data.tableRowMode === 'edit-sub-row' && row.depth === 1,
    [rowHighlighted, data, row],
  );
  const newRowVisible = useMemo(
    () => data.subRowId && row.original.id === data.subRowId && data.tableRowMode === 'create-sub-row',
    [data, row],
  );

  return newRowVisible ? (
    <div className={classNames(s.GridRow, rowProps.className)} {...rowProps}>
      <NewRow
        key={newSubRowKey}
        visible={data.tableRowMode === 'create-sub-row'}
        columns={data.subRowColumns}
        onCreate={(row) => data.handleCreateSubRow(row)}
        onHide={() => data.onHideEditRow()}
        data={data.newRowData}
        onFormChange={(row) => data.onNewRowChange(row)}
        width={data.totalColumnsWidth}
        hiddenColumns={data.hiddenColumns}
      />
    </div>
  ) : (
    <div
      onDoubleClick={() => data.onRowDoubleClick(row.original, row.id)}
      className={classNames(s.GridRow, rowProps.className)}
      {...rowProps}
      onClick={() => data.onClickRow(row.id)}
      id={row.id}
    >
      <GridRow
        row={row}
        allColumns={data.allColumns}
        editEnabled={editEnabled}
        onEditRow={(row) => data.handleEditRow(row)}
        onFormChange={(row) => data.onEditRowChange(row)}
        onHideEditRow={() => data.onHideEditRow()}
        editedRow={data.editRowData}
        subRowColumns={data.subRowColumns}
        subRowEditEnabled={subRowEditEnabled}
        onEditSubRow={(row) => data.handleEditSubRow(row)}
        hiddenColumns={data.hiddenColumns}
        key={'gridRow-' + row.id}
      />
      <div
        className={classNames(
          s.Substrate,
          rowHighlighted && classNames(s.SubstrateHighlighted, 'row-substrate-highlighted'),
          'row-substrate',
        )}
        style={{ width: data.totalColumnsWidth }}
      />
    </div>
  );
});

export const GridRowRenderer = memo(GridRowRendererInner, areEqual);
